import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ALL_OPTION_KEY, DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { ReportSummaryResponse } from '@/modules/Reports/report.types';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportSummaryQuery } from '../../queries';
import { ClaimsActivitySummaryReportParams } from '../ClaimsActivityReport.types';

export const QUERY_KEY = 'GET_REPORT_CLAIMS_ACTIVITY_SUMMARY';
interface ClaimResponse extends ReportSummaryResponse {
  date_from: string;
  date_to: string;
}
const useClaimsActivityReportSummaryQuery = (params: ClaimsActivitySummaryReportParams) => {
  const getEmployerIdParam = useGetEmployerIdParam();

  const {
    data,
    rawData,
    isLoading,
    isLoadingMore,
    isSuccess,
    isEmptyData,
    lastTimeUpdateDate,
    planOptions,
    isHasNextPage,
    handleLoadMore,
    resetPagination,
  } = useBaseReportSummaryQuery<ClaimResponse>(
    params.isMultipleEmployer
      ? API_URL.REPORTS_CLAIM_ACTIVITY_REPORT_MULTI_EMPLOYERS_SUMMARY
      : API_URL.REPORTS_CLAIM_ACTIVITY_REPORT_SUMMARY,
    [
      QUERY_KEY,
      params.employerId,
      params?.startDate,
      params?.endDate,
      params?.claimStatuses.join(','),
      params?.claimTypes.join(','),
      params.employerIds?.join(','),
    ],
    {
      ...getEmployerIdParam(params.employerId, params.employerIds),
      claim_statuses:
        params?.claimStatuses && !params?.claimStatuses.includes(ALL_OPTION_KEY)
          ? params?.claimStatuses.join(',')
          : undefined,
      claim_types:
        params?.claimTypes && !params?.claimTypes.includes(ALL_OPTION_KEY)
          ? params?.claimTypes.join(',')
          : undefined,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      employerId: params.employerId,
      employerIds: params.employerIds,
    }
  );

  const claimStatusesKey = params?.claimStatuses.join(',');
  const claimTypesKey = params?.claimTypes.join(',');
  const employerIdsKey = params.employerIds?.join(',');

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    params?.startDate,
    params?.endDate,
    claimStatusesKey,
    claimTypesKey,
    employerIdsKey,
  ]);

  const [fromDate, toDate] = useMemo(() => {
    const response = rawData?.data;
    const from = response?.date_from
      ? dayjs(response.date_from, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
      : null;
    const to = response?.date_to
      ? dayjs(response.date_to, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
      : null;
    return [from, to];
  }, [rawData?.data]);
  const isEmpty = useMemo(() => {
    return !toDate && !fromDate;
  }, [fromDate, toDate]);
  const title = useMemo(() => {
    if (isEmpty) {
      return null;
    }
    if (fromDate || toDate) {
      return `${fromDate || EMPTY_FIELD_VALUE} — ${toDate || EMPTY_FIELD_VALUE}`;
    }
    return EMPTY_FIELD_VALUE;
  }, [fromDate, isEmpty, toDate]);

  return {
    lastTimeUpdateDate,
    planOptions,
    data,
    isLoading,
    isLoadingMore,
    isSuccess,
    isEmptyData,
    title,
    isHasNextPage,
    handleLoadMore,
  };
};

export default useClaimsActivityReportSummaryQuery;
